import { useEffect, useState } from "react";
import useGlobalContext from "../../../hooks/useGlobalContext";
import ErrorMessage from "../../../components/common/ErrorMessage";
import ModalBackground from "../../../components/common/ModalBackground";
import { Card, CardContent, Chip } from "@mui/material";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../../components/SearcherAndAdd";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../../components/common/TableComponent";
import PagePagination from "../../../components/PagePagination";
import localize from "../../../common/localize";
import {
  getClearInventoryMovementType,
  getInventoryMovementTypeFilteringOptions,
  getMovementTypeColor,
  getMovementTypeName,
} from "../../../common/EntitiesManagement";
import AddOrEditInventoryMovementType from "./AddOrEditInventoryMovementType";
import { generateNoDataValues, getFiltersInfo } from "../../../common/utils";
import { mutate } from "swr";
import ActionButtons from "../../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import TrueFalseImage from "../../../components/TrueFalseImage";
import EntityPrinter from "../../../components/common/EntityPrinter";

export default function InventoryMovementType() {
  const {
    inventoryMovementTypeModel,
    setInventoryMovementTypeModel,
    inventoryMovementTypeData,
    INVENTORY_MOVEMENT_TYPE_ENDPOINT,
    config,
    pageIndexInventoryMovementType,
    setPageIndexInventoryMovementType,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const [
    inventoryMovementTypeDataFiltered,
    setinventoryMovementTypeDataFiltered,
  ] = useState(inventoryMovementTypeData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getInventoryMovementTypeFilteringOptions());
  }, []);

  useEffect(() => {
    setinventoryMovementTypeDataFiltered(inventoryMovementTypeData.Data);
  }, [inventoryMovementTypeData]);

  const validateOperationResult = (result, isDelete) => {
    setInventoryMovementTypeModel(getClearInventoryMovementType());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([INVENTORY_MOVEMENT_TYPE_ENDPOINT, config]);

    if (isDelete) {
      if (
        pageIndexInventoryMovementType !== 0 &&
        inventoryMovementTypeData.Data.length === 1
      ) {
        setPageIndexInventoryMovementType(pageIndexInventoryMovementType - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${INVENTORY_MOVEMENT_TYPE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${INVENTORY_MOVEMENT_TYPE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${INVENTORY_MOVEMENT_TYPE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.type,
      localize.referenced_by_usage,
      localize.notification,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.inventory_movement_type}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={INVENTORY_MOVEMENT_TYPE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="inventory_movement_type"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_inventory_movement_type}
          onClickToSubmit={() => {
            handleUpdateRequest(inventoryMovementTypeModel);
            setShowEditModal(false);
            setInventoryMovementTypeModel(getClearInventoryMovementType());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setInventoryMovementTypeModel(getClearInventoryMovementType());
          }}
          Component={
            <AddOrEditInventoryMovementType
              model={inventoryMovementTypeModel}
              setModel={setInventoryMovementTypeModel}
            />
          }
          validators={
            !inventoryMovementTypeModel.Name ||
            inventoryMovementTypeModel.Name.length === 0 ||
            inventoryMovementTypeModel.MovementType === 0
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.inventory_movement_type_title}
            subtitle={localize.inventory_movement_type_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="inventory_movement_type_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                validators={
                  !inventoryMovementTypeModel.Name ||
                  inventoryMovementTypeModel.Name.length === 0 ||
                  inventoryMovementTypeModel.MovementType === 0
                }
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_inventory_movement_type}
                onClickToSubmit={() => {
                  handleCreateRequest(inventoryMovementTypeModel);
                  setShowAddModal(false);
                  setInventoryMovementTypeModel(
                    getClearInventoryMovementType()
                  );
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setInventoryMovementTypeModel(
                    getClearInventoryMovementType()
                  );
                }}
                Component={
                  <AddOrEditInventoryMovementType
                    model={inventoryMovementTypeModel}
                    setModel={setInventoryMovementTypeModel}
                  />
                }
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={inventoryMovementTypeModel.Name}
          entityType={localize.inventory_movement_type_title}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setInventoryMovementTypeModel(getClearInventoryMovementType());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(inventoryMovementTypeModel.Id);
            setShowDeleteModal(false);
            setInventoryMovementTypeModel(getClearInventoryMovementType());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexInventoryMovementType}
            setPageIndex={setPageIndexInventoryMovementType}
            dataArray={inventoryMovementTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          inventoryMovementTypeData.Data?.length !== 0
            ? inventoryMovementTypeDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setInventoryMovementTypeModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setInventoryMovementTypeModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <Chip
                    className="chipContainer"
                    style={{
                      backgroundColor: getMovementTypeColor(item.MovementType),
                      color: "white",
                      borderColor: getMovementTypeColor(item.MovementType),
                    }}
                    label={getMovementTypeName(item.MovementType)}
                  />,
                  <TrueFalseImage value={item.referencedByUsage} />,
                  <TrueFalseImage value={item.createNotification} />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
